import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 aria-invalid:border-destructive cursor-pointer',
  {
    variants: {
      variant: {
        // Primary action colour: --brand (#0062e7 light / #2953e3 dark), NOT --brand-solid. Every
        // primary button in the app resolves to this, so a `<Button>` with no bg override is the
        // correct default rather than something to be overridden per screen.
        default: 'bg-brand text-white hover:bg-brand-hover',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/**
 * @param {{ variant?: string, size?: string, asChild?: boolean, className?: string, dataTestId?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>} props
 */
function Button({ className, variant, size, asChild = false, dataTestId, ...props }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      data-testid={dataTestId}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
